import React, { FunctionComponent, ReactNode } from 'react';
import Button, { ButtonMainStyle } from 'shared/components/atoms/Button';
import SectionHeader from 'shared/components/atoms/SectionHeader';
import Column from 'shared/components/atoms/Column';
import Icon from 'shared/components/atoms/Icon';
import styled from 'styled-components';
import Alert, { Alerts } from 'shared/components/atoms/Alert';
import { SupplierList } from 'Admin/preferences/SupplierPreferences/SupplierPreferences';
import { presentSupplierName } from 'Quotes/utils/supplierHelpers';

type Props = {
  className?: string;
  introText: ReactNode;
  suppliers: SupplierList[];
  updatePageStatus(readOnly: boolean, saved: boolean): void;
  saved: boolean;
  cloudConnectProviders?: SupplierList[];
};

const UnstyledReadOnlyPreferences: FunctionComponent<React.PropsWithChildren<Props>> = ({
  className,
  introText,
  suppliers,
  updatePageStatus,
  saved,
  cloudConnectProviders,
}) => {
  const columns = 3;

  const onEditButtonClick = () => {
    updatePageStatus(false, false);
  };

  return (
    <div className={className}>
      <div className="row no-gutters button-wrapper">
        <div className="col-1 offset-10">
          <Button
            data-testid="preferences-button-edit"
            mainStyle={ButtonMainStyle.PrimaryOutline}
            disabled={false}
            onClick={onEditButtonClick}
          >
            Edit
          </Button>
        </div>
      </div>

      <SectionHeader text="Supplier Preferences" />

      {saved && <Alert alertType={Alerts.SUCCESS}>Your preferences have been saved</Alert>}

      {introText}

      <SectionHeader text="Last Mile Providers" className="mt-5" />
      <Column defaultWidth={12}>
        <div data-testid="preferences-readonly" className="row no-gutters">
          {[...Array(columns).keys()].map((c) => {
            const suppliersInColumn = suppliers.filter((_, i) => i % columns === c);
            return (
              <Column key={'col' + c} defaultWidth={12 / columns} classNames={['admin']}>
                {suppliersInColumn.map((supplier, i_inner) => (
                  <div key={'col' + c + '-' + i_inner} className="row no-gutters">
                    <span className="option">
                      <Icon name={supplier.status ? 'done' : 'close'} className="grey10" />
                      {supplier.key === 'ITS' ? 'ITS' : presentSupplierName(supplier.value)}
                    </span>
                  </div>
                ))}
              </Column>
            );
          })}
        </div>
      </Column>

      {cloudConnectProviders && cloudConnectProviders.length > 0 && (
        <>
          <SectionHeader text="Cloud Connect Providers" className="mt-5" />
          <Column defaultWidth={12}>
            <div data-testid="cloud-connect-providers" className="row no-gutters">
              {[...Array(columns).keys()].map((c) => {
                const providersInColumn = cloudConnectProviders.filter((_, i) => i % columns === c);
                return (
                  <Column key={'cloud-col' + c} defaultWidth={12 / columns} classNames={['admin']}>
                    {providersInColumn.map((provider, i_inner) => (
                      <div key={'cloud-col' + c + '-' + i_inner} className="row no-gutters">
                        <span className="option">
                          {<Icon name={provider.status ? 'done' : 'close'} className="grey10" />}
                          {provider.value}
                        </span>
                      </div>
                    ))}
                  </Column>
                );
              })}
            </div>
          </Column>
        </>
      )}
    </div>
  );
};

const ReadOnlyPreferences = styled(UnstyledReadOnlyPreferences)`
  .option {
    i {
      margin-right: 0.2em;
      vertical-align: middle;
    }
  }
`;

export { ReadOnlyPreferences };
